/*
  Session end → sign in → back where you were (01-login idea 5).

  Two ways a session ends while someone is working:
  1. The token runs out (8 h). AuthContext knows the token's `exp` (moved onto this device's clock, see
     toDeviceTimes) and ends the session itself at that moment; the shell (Layout) then opens
     /login?next=<page>&reason=expired and leaves a notice here.
  2. The server answers 401 for another reason (account removed, token refused): lib/api.js reloads the
     browser on /login?next=<page> (when a session was stored). Layout also keeps the page the user is on in
     this tab's sessionStorage, so the sign-in page can still say "Sign in to return to LIV Waterside ›
     Stages" after that reload.

  Everything is per browser tab (sessionStorage), never sent anywhere, and only same-origin app paths are
  ever used as a return target (no open redirect).
*/

const RETURN_KEY = 'sara.session.returnTo';
const NOTICE_KEY = 'sara.session.notice';
const MAX_PATH_LENGTH = 2048;
export const DEFAULT_SESSION_HOURS = 8;

const storage = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null;
  } catch {
    return null;
  }
};

const readJson = (key) => {
  try {
    const raw = storage()?.getItem(key);
    if (!raw) return null;
    const value = JSON.parse(raw);
    return value && typeof value === 'object' ? value : null;
  } catch {
    return null;
  }
};

const writeJson = (key, value) => {
  try {
    storage()?.setItem(key, JSON.stringify(value));
  } catch {
    // storage blocked or full — returning to the page is only a convenience
  }
};

const remove = (key) => {
  try {
    storage()?.removeItem(key);
  } catch {
    // nothing stored then either
  }
};

/** exp / iat of a JWT in epoch ms ({ exp, iat }), or null for anything that is not a readable JWT. */
export function decodeTokenTimes(token) {
  if (typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    const json = typeof atob === 'function' ? atob(padded) : Buffer.from(padded, 'base64').toString('binary');
    const payload = JSON.parse(json);
    const exp = Number(payload?.exp);
    const iat = Number(payload?.iat);
    if (!Number.isFinite(exp) || exp <= 0) return null;
    return { exp: exp * 1000, iat: Number.isFinite(iat) && iat > 0 ? iat * 1000 : null };
  } catch {
    return null;
  }
}

/*
  Clock skew. `exp` / `iat` are the SERVER's clock; this device's clock may be hours off (a site tablet set
  to the wrong time zone). Comparing exp with Date.now() directly ended a fresh session at once on a device
  more than 8 h ahead (sign in → straight back to an empty sign-in page). At sign-in the token was issued
  "just now", so `now - iat` is how far this device runs ahead of the server; it is kept with the token and
  every session time the app uses is on the device's own clock.
*/

/** How far this device's clock runs ahead of the server's (ms; negative when behind), measured at sign-in. */
export function measureClockOffset(times, now = Date.now()) {
  if (!times?.iat || !Number.isFinite(now)) return 0;
  return now - times.iat;
}

/**
 * Server-clock token times → this device's clock ({ exp, iat } in device ms), or null when the token has
 * no usable lifetime (then only the server decides when the session ends: no client-side expiry).
 */
export function toDeviceTimes(times, offsetMs = 0) {
  if (!times?.exp) return null;
  if (times.iat && times.exp <= times.iat) return null;
  const offset = Number.isFinite(offsetMs) ? offsetMs : 0;
  return { exp: times.exp + offset, iat: times.iat ? times.iat + offset : null };
}

/** "8 hours" — how long a session lasts (from the token), for the sign-in notice. */
export function sessionLengthText(times) {
  const hours = times?.exp && times?.iat ? Math.round((times.exp - times.iat) / 3_600_000) : DEFAULT_SESSION_HOURS;
  const value = hours > 0 ? hours : DEFAULT_SESSION_HOURS;
  return `${value} ${value === 1 ? 'hour' : 'hours'}`;
}

/** Only an in-app path may be a return target: "/projects/1?tab=stages" yes; "//evil.com", "https://…", "/login" no. */
export function isSafeReturnPath(path) {
  if (typeof path !== 'string' || !path || path.length > MAX_PATH_LENGTH) return false;
  if (!path.startsWith('/') || path.startsWith('//') || path.startsWith('/\\')) return false;
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f\\]/.test(path)) return false;
  const pathname = path.split(/[?#]/)[0];
  return pathname !== '/login' && !pathname.startsWith('/login/');
}

const PAGE_LABELS = [
  ['/projects/', 'your project'],
  ['/projects', 'Projects'],
  ['/tasks', 'Tasks'],
  ['/shop-drawings', 'Shop Drawings'],
  ['/documents', 'Documents'],
  ['/inventory', 'Inventory'],
  ['/procurement', 'Procurement'],
  ['/forecast', 'Forecast'],
  ['/invoicing', 'Invoicing'],
  ['/ai', 'AI'],
  ['/admin', 'Admin'],
];

/** A readable name for a return path when the page's own breadcrumb is not known. */
export function labelForPath(path) {
  if (!isSafeReturnPath(path)) return '';
  const pathname = path.split(/[?#]/)[0];
  if (pathname === '/') return 'the Dashboard';
  const match = PAGE_LABELS.find(([prefix]) => (
    prefix.endsWith('/') ? pathname.startsWith(prefix) : pathname === prefix || pathname.startsWith(`${prefix}/`)
  ));
  return match ? match[1] : '';
}

/** The page this tab is on (kept up to date by the shell while signed in). */
export function rememberReturnTo({ path, label, times }) {
  if (!isSafeReturnPath(path)) return;
  writeJson(RETURN_KEY, {
    path,
    label: typeof label === 'string' ? label.slice(0, 160) : '',
    exp: times?.exp || null,
    iat: times?.iat || null,
  });
}

export const readReturnTo = () => {
  const value = readJson(RETURN_KEY);
  return value && isSafeReturnPath(value.path) ? value : null;
};

/** Left by the shell when it ends an expired session: { reason, path, label, length }. */
export function writeSessionNotice({ reason = 'expired', path, label, times }) {
  writeJson(NOTICE_KEY, {
    reason,
    path: isSafeReturnPath(path) ? path : '',
    label: typeof label === 'string' ? label.slice(0, 160) : '',
    length: sessionLengthText(times),
    at: Date.now(),
  });
}

export const readSessionNotice = () => readJson(NOTICE_KEY);

/** Explicit sign-out / successful sign-in: nothing to return to any more. */
export function clearSessionMemory() {
  remove(RETURN_KEY);
  remove(NOTICE_KEY);
}

/**
 * What the sign-in page should say and where it should go after signing in.
 * `search` = location.search of /login; `hasToken` = a token is still stored (then nothing "ended").
 * Returns { reason: 'expired' | 'signed-out' | null, path, label, length }.
 */
export function resolveSignInReturn(search, { hasToken = false, now = Date.now() } = {}) {
  const params = new URLSearchParams(search || '');
  const nextParam = params.get('next');
  const reasonParam = params.get('reason');
  const notice = readSessionNotice();
  const remembered = hasToken ? null : readReturnTo();

  const path = (isSafeReturnPath(nextParam) && nextParam)
    || (notice && isSafeReturnPath(notice.path) && notice.path)
    || (remembered && remembered.path)
    || '';

  let reason = null;
  if (reasonParam === 'expired' || reasonParam === 'signed-out') reason = reasonParam;
  else if (notice?.reason) reason = notice.reason === 'signed-out' ? 'signed-out' : 'expired';
  else if (remembered) reason = remembered.exp && now >= remembered.exp - 5000 ? 'expired' : 'signed-out';

  const sameAs = (record) => record && record.path === path && record.label;
  const label = (sameAs(notice) && notice.label) || (sameAs(remembered) && remembered.label) || labelForPath(path);
  const length = notice?.length || sessionLengthText(remembered);
  return { reason, path, label, length };
}

/** Leaving the app for another site (Microsoft 365 sign-in). An object, so tests can replace `assign`. */
export const browserNavigation = {
  assign: (url) => window.location.assign(url),
};
